import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, Users, Clock, Zap, Target, 
  Award, Crown, ChevronRight, Brain 
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

export function PredictionContests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: contests, isLoading } = useQuery({
    queryKey: ['prediction-contests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prediction_contests')
        .select('*')
        .eq('is_active', true)
        .order('starts_at', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: myEntries } = useQuery({
    queryKey: ['my-contest-entries', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contest_entries')
        .select('*, prediction_contests(*)')
        .eq('user_id', user?.id);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const joinContest = useMutation({
    mutationFn: async (contestId: string) => {
      const { error } = await supabase.from('contest_entries').insert({
        contest_id: contestId,
        user_id: user?.id,
        predictions: {},
      });
      if (error) throw error;
      
      await supabase
        .from('prediction_contests')
        .update({ current_participants: (contests?.find(c => c.id === contestId)?.current_participants || 0) + 1 })
        .eq('id', contestId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prediction-contests'] });
      queryClient.invalidateQueries({ queryKey: ['my-contest-entries'] });
      toast.success('Joined contest!');
    },
    onError: () => toast.error('Failed to join contest'),
  });

  const isJoined = (contestId: string) => myEntries?.some(e => e.contest_id === contestId);

  const getContestIcon = (type: string) => {
    switch (type) {
      case 'weekly':
        return <Trophy className="h-5 w-5" />;
      case 'weekend':
        return <Zap className="h-5 w-5" />;
      default:
        return <Target className="h-5 w-5" />;
    }
  };

  const getContestColor = (type: string) => {
    switch (type) {
      case 'weekly':
        return 'from-amber-500/20 to-orange-500/20 border-amber-500/30';
      case 'weekend':
        return 'from-purple-500/20 to-pink-500/20 border-purple-500/30';
      default:
        return 'from-blue-500/20 to-cyan-500/20 border-blue-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <Card className="glass-card overflow-hidden">
        <div className="bg-gradient-to-r from-amber-600/20 via-orange-500/20 to-red-500/20 p-6">
          <div className="flex items-center gap-3">
            <Trophy className="h-8 w-8 text-amber-500" />
            <div>
              <h2 className="text-2xl font-bold">Prediction Contests</h2>
              <p className="text-muted-foreground">
                Compete with the community and win prizes
              </p>
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <Badge variant="outline" className="bg-background/50">
              <Users className="h-3 w-3 mr-1" />
              Community Events
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <Brain className="h-3 w-3 mr-1" />
              AI vs Humans
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <Award className="h-3 w-3 mr-1" />
              Win Prizes
            </Badge>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading contests...</div>
      ) : (
        <div className="grid gap-4">
          {contests?.map((contest) => (
            <Card
              key={contest.id}
              className={`glass-card border bg-gradient-to-r ${getContestColor(contest.contest_type)}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-primary/20">
                        {getContestIcon(contest.contest_type)}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{contest.title}</h3>
                        <p className="text-sm text-muted-foreground">{contest.description}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="secondary">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDistanceToNow(new Date(contest.ends_at), { addSuffix: true })}
                      </Badge>
                      {contest.sport && (
                        <Badge variant="outline">{contest.sport}</Badge>
                      )}
                      <Badge variant="outline" className="bg-green-500/20 text-green-400">
                        ${contest.prize_pool} Prize Pool
                      </Badge>
                      {contest.entry_fee > 0 && (
                        <Badge variant="outline">${contest.entry_fee} Entry</Badge>
                      )}
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Participants</span>
                        <span>{contest.current_participants}/{contest.max_participants}</span>
                      </div>
                      <Progress 
                        value={(contest.current_participants / contest.max_participants) * 100} 
                        className="h-2"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 lg:w-48">
                    {isJoined(contest.id) ? (
                      <>
                        <Button className="w-full" variant="secondary">
                          <Target className="h-4 w-4 mr-2" />
                          Make Predictions
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        </Button>
                        <p className="text-xs text-center text-muted-foreground">
                          You're in! Score: {myEntries?.find(e => e.contest_id === contest.id)?.score || 0}
                        </p>
                      </>
                    ) : (
                      <Button 
                        onClick={() => joinContest.mutate(contest.id)}
                        disabled={contest.current_participants >= contest.max_participants}
                        className="w-full"
                      >
                        <Crown className="h-4 w-4 mr-2" />
                        {contest.entry_fee > 0 ? `Join ($${contest.entry_fee})` : 'Join Free'}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {myEntries && myEntries.length > 0 && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" />
              My Active Contests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myEntries.map((entry: any) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{entry.prediction_contests?.title}</p>
                    <p className="text-sm text-muted-foreground">
                      Rank: #{entry.rank || '—'} • Score: {entry.score}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm">
                    View <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
